import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Keyboard, Platform, TouchableOpacity, View } from 'react-native';
import * as ImagePicker from 'expo-image-picker';
import { useTranslation } from 'react-i18next';
import { Image } from 'expo-image';

import { ButtonWrapper } from '@/components/ui/ButtonWrapper';
import { MenuItemIcon } from '@/components/ui/MenuItemIcon';
import {
  SlideMenu,
  SlideMenuItem,
  SlideMenuItemLabel,
  type SlideMenuPlacement,
} from '@/components/ui/slide-menu';
import { Spinner } from '@/components/gluestack/spinner';
import { useToastMessages } from '@/hooks/useToastMessages';
import { useTheme } from '@/components/providers/ThemeProvider';
import { ImagePreviewSheet } from './ImagePreviewSheet';
import { resizeImageForUpload } from './resizeImageForUpload';
import { HugeIconsWrapper } from '@/components/ui/HugeIconsWrapper';
import {
  Add01Icon,
  Camera01Icon,
  Album02Icon,
} from '@hugeicons-pro/core-stroke-rounded';
import { Cancel01Icon } from '@hugeicons-pro/core-solid-rounded';
import { useIsMobileWeb } from '@/hooks/useIsMobileWeb';

export interface IImageUploadButtonProps {
  /**
   * The upload mode:
   * - 'preview': Shows preview sheet before upload (current flow)
   * - 'direct': Passes image URI directly to caller for immediate upload
   * - 'inline-preview': Shows inline preview in place of button with cancel option
   */
  mode?: 'preview' | 'inline-preview' | 'direct';

  /**
   * Called when image is picked and ready for upload
   * In 'preview' mode: called after user confirms in preview
   * In 'direct' mode: called immediately after image is picked
   * In 'inline-preview' mode: called when user picks image (before showing inline preview)
   */
  onImageReady?: (imageUri: string | File, options?: { caption?: string }) => void;

  /**
   * Called when user cancels image selection in 'inline-preview' mode
   */
  onImageCancel?: () => void;

  /**
   * The currently selected image (for inline-preview mode)
   * This should be managed by the parent component
   */
  inlinePreviewImage?: string | File | null;

  /**
   * Whether the button should be disabled
   */
  isDisabled?: boolean;

  /**
   * Whether upload is in progress (shows spinner)
   */
  isUploading?: boolean;

  /**
   * Button size
   */
  size?: 'sm' | 'md' | 'lg';

  /**
   * Additional CSS classes (will apply to button and spinner)
   */
  className?: string;

  /**
   * Optional caption value to control from parent when using preview mode
   */
  caption?: string;

  /**
   * Called when caption changes while in preview mode
   */
  onCaptionChange?: (text: string) => void;

  /**
   * Optional custom trigger renderer (e.g., render a FAB)
   */
  renderTrigger?: (triggerProps: any) => React.ReactNode;

  /**
   * Optional title for the preview sheet
   */
  previewTitle?: string;

  /**
   * Whether to show the caption textarea in the preview sheet (preview mode only)
   */
  showCaptionInPreview?: boolean;

  /**
   * Whether to show the photo policy notice in the preview sheet (preview mode only)
   */
  showPolicyNotice?: boolean;

  /**
   * On native platforms, dismiss the on-screen keyboard right before launching
   * the image picker. Useful for surfaces where the picker is triggered from a
   * focused text input (e.g. messaging) and the keyboard should not remain
   * visible while/after picking. Defaults to false to preserve the current
   * behavior on screens like Add Post that intentionally keep the keyboard open.
   */
  dismissKeyboardOnPick?: boolean;

  /**
   * Placement of the menu relative to the trigger (e.g., 'top left', 'top right')
   */
  placement?: SlideMenuPlacement;
}

/**
 * Detects if camera is available on web
 */
const useCameraAvailable = () => {
  const isMobileWeb = useIsMobileWeb();
  const [isCameraAvailable, setIsCameraAvailable] = useState(Platform.OS !== 'web');

  useEffect(() => {
    if (Platform.OS !== 'web') {
      setIsCameraAvailable(true);
      return;
    }

    // On web, expo-image-picker uses HTML file input with capture="camera" attribute
    // This only works reliably on mobile browsers, not desktop browsers
    setIsCameraAvailable(isMobileWeb);
  }, [isMobileWeb]);

  return isCameraAvailable;
};

/**
 * Hook that handles image picking logic
 */
const useImagePicker = () => {
  const { showErrorToast } = useToastMessages();
  const { t } = useTranslation();

  const requestPermissions = useCallback(
    async (source: 'camera' | 'gallery') => {
      if (Platform.OS === 'web') return true;

      if (source === 'camera') {
        const cameraPermission = await ImagePicker.getCameraPermissionsAsync();

        if (cameraPermission.status !== 'granted') {
          const { status } = await ImagePicker.requestCameraPermissionsAsync();

          if (status !== 'granted') {
            showErrorToast(
              t('messages.cameraPermissionNotGranted', 'Camera permission not granted')
            );
            return false;
          }

          // Add small delay after first-time permission grant on iOS
          if (Platform.OS === 'ios') {
            await new Promise((resolve) => setTimeout(resolve, 100));
          }
        }
      } else {
        const libraryPermission = await ImagePicker.getMediaLibraryPermissionsAsync();

        if (libraryPermission.status !== 'granted') {
          const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();

          if (status !== 'granted') {
            showErrorToast(
              t('messages.mediaLibraryPermissionNotGranted', 'Media library permission not granted')
            );
            return false;
          }

          // Add small delay after first-time permission grant on iOS
          if (Platform.OS === 'ios') {
            await new Promise((resolve) => setTimeout(resolve, 100));
          }
        }
      }
      return true;
    },
    [showErrorToast]
  );

  const pickImage = useCallback(
    async (source: 'camera' | 'gallery') => {
      const hasPermission = await requestPermissions(source);
      if (!hasPermission) return null;

      const options: ImagePicker.ImagePickerOptions = {
        mediaTypes: ['images'],
        allowsEditing: false,
        quality: 0.7,
      };

      try {
        let result: ImagePicker.ImagePickerResult;

        if (source === 'camera') {
          result = await ImagePicker.launchCameraAsync(options);
        } else {
          result = await ImagePicker.launchImageLibraryAsync(options);
        }

        if (!result.canceled) {
          const asset = result.assets[0];
          const original = Platform.OS === 'web' && asset.file ? asset.file : asset.uri;

          const resized = await resizeImageForUpload(original, asset.width, asset.height);
          if (resized !== original) return resized;

          // Check file size (16MB limit)
          const maxSize = 16 * 1024 * 1024; // 16MB in bytes
          const maxSizeMB = Math.round(maxSize / (1024 * 1024)); // Convert to MB for display
          let fileSize = 0;

          if (Platform.OS === 'web' && asset.file) {
            fileSize = asset.file.size;
          } else if (asset.fileSize) {
            fileSize = asset.fileSize;
          }

          if (fileSize > maxSize) {
            showErrorToast(
              t('messages.imageTooLarge', 'Image is too large. Maximum size is {{size}}MB.', {
                size: maxSizeMB,
              })
            );
            return null;
          }

          // On web, return the File object if available, otherwise return the URI
          return original;
        }
      } catch (error) {
        console.warn('Image picker error:', error);
        showErrorToast(t('messages.failedToPickImage', 'Failed to pick image'));
      }

      return null;
    },
    [requestPermissions, showErrorToast, t]
  );

  return { pickImage };
};

export function ImageUploadButton({
  mode = 'preview',
  onImageReady,
  onImageCancel,
  inlinePreviewImage: inlinePreviewImageProp,
  isDisabled = false,
  isUploading = false,
  size = 'lg',
  className = '',
  caption,
  onCaptionChange,
  renderTrigger,
  previewTitle,
  placement = 'top left',
  showCaptionInPreview = true,
  showPolicyNotice = false,
  dismissKeyboardOnPick = false,
}: IImageUploadButtonProps) {
  const { t } = useTranslation();
  const { getThemeColor } = useTheme();
  const isCameraAvailable = useCameraAvailable();
  const { pickImage } = useImagePicker();

  // Internal state only used for 'preview' mode (preview sheet)
  const [internalSelectedImage, setInternalSelectedImage] = useState<string | File | null>(null);

  // For inline-preview mode, use the prop. For preview mode, use internal state
  const selectedImage = mode === 'inline-preview' ? inlinePreviewImageProp : internalSelectedImage;

  const previewImageUri = useMemo(() => {
    if (!selectedImage) return null;
    if (typeof selectedImage === 'string') return selectedImage;
    return URL.createObjectURL(selectedImage);
  }, [selectedImage]);

  useEffect(() => {
    if (previewImageUri && selectedImage && typeof selectedImage !== 'string') {
      return () => URL.revokeObjectURL(previewImageUri);
    }
  }, [previewImageUri, selectedImage]);

  const handleImagePick = useCallback(
    async (source: 'camera' | 'gallery') => {
      if (dismissKeyboardOnPick && Platform.OS !== 'web') {
        Keyboard.dismiss();
      }

      const image = await pickImage(source);

      if (image) {
        if (mode === 'direct') {
          // Direct mode: immediately call onImageReady
          onImageReady?.(image);
        } else if (mode === 'inline-preview') {
          // Inline-preview mode: parent manages state, just call onImageReady
          onImageReady?.(image);
        } else {
          // Preview mode: set internal state for preview sheet
          setInternalSelectedImage(image);
        }
      }
    },
    [pickImage, mode, onImageReady, dismissKeyboardOnPick]
  );

  const handleImageConfirm = useCallback(() => {
    if (selectedImage) {
      onImageReady?.(selectedImage, { caption });
      if (mode !== 'inline-preview') {
        setInternalSelectedImage(null);
      }
    }
  }, [selectedImage, onImageReady, mode, caption]);

  const handleImageCancel = useCallback(() => {
    if (mode === 'inline-preview') {
      // For inline-preview mode, parent handles state
      onImageCancel?.();
    } else {
      // For other modes, clear internal state
      setInternalSelectedImage(null);
      onImageCancel?.();
    }
  }, [onImageCancel, mode]);

  if (isUploading && !renderTrigger) {
    return (
      <Spinner
        color={getThemeColor('secondary', '500')}
        className={`h-10 w-10 self-center ${className}`}
      />
    );
  }

  // Show inline preview in inline-preview mode when image is selected
  if (mode === 'inline-preview' && selectedImage && previewImageUri) {
    return (
      <View className={`relative h-24 w-24 self-center ${className}`}>
        <Image source={{ uri: previewImageUri }} className="h-full w-full rounded-lg" contentFit="cover" />
        <TouchableOpacity
          onPress={handleImageCancel}
          className="absolute -right-2.5 -top-2.5 rounded-full bg-neutral-800 p-1 dark:bg-black">
          <HugeIconsWrapper
            icon={Cancel01Icon}
            size={14}
            className="text-white dark:text-typography-700"
          />
        </TouchableOpacity>
      </View>
    );
  }

  const menuContent = (
    <>
      <SlideMenu
        placement={placement}
        offset={12}
        disabled={isDisabled}
        dismissKeyboardBeforeOpen={Platform.OS === 'android'}
        triggerWrapperClassName={
          renderTrigger && Platform.OS !== 'web' ? 'absolute bottom-4 right-4 z-20' : undefined
        }
        trigger={(triggerProps) =>
          renderTrigger ? (
            renderTrigger(triggerProps)
          ) : (
            <ButtonWrapper
              {...triggerProps}
              icon={Add01Icon}
              size={size}
              variant="outline"
              action="primary"
              isDisabled={isDisabled}
              className={`h-10 w-10 rounded-full ${className}`}
            />
          )
        }>
        {isCameraAvailable && (
          <SlideMenuItem key="take-photo" onPress={() => handleImagePick('camera')}>
            <MenuItemIcon icon={Camera01Icon} />
            <SlideMenuItemLabel>{t('messages.takePhoto', 'Camera')}</SlideMenuItemLabel>
          </SlideMenuItem>
        )}
        <SlideMenuItem key="pick-library" onPress={() => handleImagePick('gallery')}>
          <MenuItemIcon icon={Album02Icon} />
          <SlideMenuItemLabel>{t('messages.selectPhoto', 'Photos')}</SlideMenuItemLabel>
        </SlideMenuItem>
      </SlideMenu>

      {/* Preview mode: show preview sheet when image is selected */}
      {mode === 'preview' && selectedImage && previewImageUri && (
        <ImagePreviewSheet
          imageUri={previewImageUri}
          onConfirm={handleImageConfirm}
          onCancel={handleImageCancel}
          isUploading={isUploading}
          title={previewTitle}
          caption={caption}
          onCaptionChange={onCaptionChange}
          showCaption={showCaptionInPreview}
          showPolicyNotice={showPolicyNotice}
        />
      )}
    </>
  );

  // When using custom renderTrigger (like FAB), don't wrap in self-center View
  // to allow the trigger to position itself correctly
  if (renderTrigger) {
    return menuContent;
  }

  return <View className="self-center">{menuContent}</View>;
}
